import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { webPageSchema } from "@/components/schema";
import { SITE_TAGLINE } from "@/lib/site";

const recent = [
  {
    kicker: "Guide",
    title: "How to choose a smile-makeover clinic in Spain",
    dek: "A twelve-dimension rubric for patients in due-diligence mode — lab model, workflow, preparation philosophy, ceramist proximity, review depth.",
    href: "/how-to-choose-a-smile-makeover-clinic-in-spain",
  },
  {
    kicker: "National guide",
    title: "Smile-makeover clinics in Spain — a national editorial guide",
    dek: "Country-level orientation. Clinics grouped by city — Madrid, Barcelona, Costa del Sol, Valencia, Seville — each profiled on the same rubric.",
    href: "/best-smile-makeover-clinics-in-spain",
  },
  {
    kicker: "Comparison",
    title: "Porcelain veneer clinics in Marbella — an evaluation",
    dek: "Six Marbella-area practices examined on the same rubric, in the same order. Each clinic is linked to its own website.",
    href: "/best-veneer-clinics-in-marbella",
  },
  {
    kicker: "Essay",
    title: "Natural versus overdone veneers",
    dek: "Proportion, incisal edge, translucency, shade relationship to skin tone. The visual language of a smile that reads as real.",
    href: "/natural-vs-overdone-veneers",
  },
  {
    kicker: "Essay",
    title: "Minimal-prep versus over-prepped veneers",
    dek: "How much tooth should actually be removed, and how to tell what a clinic practices before you sit in the chair.",
    href: "/minimal-prep-vs-overprepped-veneers",
  },
  {
    kicker: "Reference",
    title: "Methodology",
    dek: "The published rubric, in full. The same twelve dimensions are applied to every clinic we examine — ACE DNTL included.",
    href: "/methodology",
  },
  {
    kicker: "Publication",
    title: "About Spain Smile Guide",
    dek: "Who publishes this site, how it is funded, and the affiliation we disclose in full.",
    href: "/about",
  },
];

export default function Home() {
  return (
    <>
      <SEO
        title="Spain Smile Guide"
        description={SITE_TAGLINE}
        path="/"
        type="website"
        jsonLd={webPageSchema(
          "Spain Smile Guide",
          SITE_TAGLINE,
          "/",
        )}
      />

      <section className="border-b border-rule">
        <div className="container-wide grid gap-10 py-20 md:grid-cols-12 md:py-32">
          <div className="md:col-span-8">
            <div className="eyebrow mb-6">Spain &middot; editorial</div>
            <h1 className="font-serif text-display text-ink">
              An editorial resource for choosing a smile-makeover clinic in
              Spain.
            </h1>
            <p className="mt-8 max-w-2xl font-serif text-[1.4rem] leading-snug text-ink/80">
              A single rubric, applied consistently to every clinic we
              examine. Long pieces, dated and revised. Named authorship.
              Disclosed affiliations. No ad units, no affiliate links, no
              sponsored placements.
            </p>
            <div className="mt-10 flex flex-wrap gap-6 font-sans text-small">
              <Link to="/how-to-choose-a-smile-makeover-clinic-in-spain">
                Read the decision framework &rarr;
              </Link>
              <Link to="/methodology">Read the methodology &rarr;</Link>
            </div>
          </div>
          <aside className="md:col-span-4 md:border-l md:border-rule md:pl-8">
            <div className="eyebrow mb-4">What this site is</div>
            <p className="text-small text-ink/80">
              A publication for patients researching a smile makeover in
              Spain. Written for the reader who intends to spend serious money
              once, and wants to choose well the first time.
            </p>
            <p className="mt-4 text-small text-ink/80">
              Updated slowly. Two to three long pieces per month. Revisions
              dated at the foot of each article.
            </p>
          </aside>
        </div>
      </section>

      <section className="border-b border-rule">
        <div className="container-wide grid gap-10 py-16 md:grid-cols-3">
          <article>
            <div className="eyebrow mb-3">Guides</div>
            <h2 className="font-serif text-h2 text-ink">
              Decision frameworks
            </h2>
            <p className="mt-3 text-ink/80">
              Long, patient-side guides written for someone choosing between
              clinics. They teach the reader to evaluate, not where to buy.
            </p>
            <Link
              to="/how-to-choose-a-smile-makeover-clinic-in-spain"
              className="mt-5 inline-block font-sans text-small"
            >
              Read &rarr;
            </Link>
          </article>
          <article>
            <div className="eyebrow mb-3">Comparisons</div>
            <h2 className="font-serif text-h2 text-ink">
              Clinic evaluations
            </h2>
            <p className="mt-3 text-ink/80">
              Named clinics evaluated on the same rubric, in the same order.
              Linked out to their own websites. No rankings, no star ratings,
              no review aggregation.
            </p>
            <Link
              to="/best-veneer-clinics-in-marbella"
              className="mt-5 inline-block font-sans text-small"
            >
              Read &rarr;
            </Link>
          </article>
          <article>
            <div className="eyebrow mb-3">Methodology</div>
            <h2 className="font-serif text-h2 text-ink">
              How we evaluate
            </h2>
            <p className="mt-3 text-ink/80">
              Twelve dimensions, published in full. The rubric does not move
              between pieces. Affiliations are disclosed above the fold on
              every comparison.
            </p>
            <Link
              to="/methodology"
              className="mt-5 inline-block font-sans text-small"
            >
              Read &rarr;
            </Link>
          </article>
        </div>
      </section>

      <section className="border-b border-rule">
        <div className="container-wide py-16">
          <div className="mb-8 flex items-end justify-between">
            <h2 className="font-serif text-h1 text-ink">
              Comparisons by city
            </h2>
            <Link
              to="/best-smile-makeover-clinics-in-spain"
              className="font-sans text-small"
            >
              National guide &rarr;
            </Link>
          </div>
          <p className="max-w-3xl text-ink/80">
            Each city evaluation applies the same twelve-dimension rubric
            in the same order. Start with the national guide for
            orientation, then drill into the city you are considering.
          </p>
          <ul className="mt-10 grid gap-x-8 gap-y-8 md:grid-cols-3">
            <li className="border-t border-rule pt-5">
              <div className="eyebrow mb-2">National</div>
              <h3 className="font-serif text-[1.25rem] leading-tight text-ink">
                <Link to="/best-smile-makeover-clinics-in-spain">
                  Smile-makeover clinics in Spain
                </Link>
              </h3>
              <p className="mt-2 text-small text-ink/80">
                Country-level orientation. Clinics grouped by city, each
                profiled briefly against the rubric.
              </p>
            </li>
            <li className="border-t border-rule pt-5">
              <div className="eyebrow mb-2">Madrid</div>
              <h3 className="font-serif text-[1.25rem] leading-tight text-ink">
                <Link to="/best-veneer-clinics-in-madrid">
                  Veneer clinics in Madrid
                </Link>
              </h3>
              <p className="mt-2 text-small text-ink/80">
                Five Madrid practices examined across the full rubric.
              </p>
            </li>
            <li className="border-t border-rule pt-5">
              <div className="eyebrow mb-2">Barcelona</div>
              <h3 className="font-serif text-[1.25rem] leading-tight text-ink">
                <Link to="/best-veneer-clinics-in-barcelona">
                  Veneer clinics in Barcelona
                </Link>
              </h3>
              <p className="mt-2 text-small text-ink/80">
                Five Barcelona practices examined across the full rubric.
              </p>
            </li>
            <li className="border-t border-rule pt-5">
              <div className="eyebrow mb-2">Marbella / Costa del Sol</div>
              <h3 className="font-serif text-[1.25rem] leading-tight text-ink">
                <Link to="/best-veneer-clinics-in-marbella">
                  Veneer clinics in Marbella
                </Link>
              </h3>
              <p className="mt-2 text-small text-ink/80">
                Six Marbella-area practices examined across the full rubric.
              </p>
            </li>
            <li className="border-t border-rule pt-5">
              <div className="eyebrow mb-2">Valencia</div>
              <h3 className="font-serif text-[1.25rem] leading-tight text-ink">
                <Link to="/best-veneer-clinics-in-valencia">
                  Veneer clinics in Valencia
                </Link>
              </h3>
              <p className="mt-2 text-small text-ink/80">
                Five Valencia practices examined across the full rubric.
              </p>
            </li>
            <li className="border-t border-rule pt-5">
              <div className="eyebrow mb-2">Seville</div>
              <h3 className="font-serif text-[1.25rem] leading-tight text-ink">
                <Link to="/best-veneer-clinics-in-seville">
                  Veneer clinics in Seville
                </Link>
              </h3>
              <p className="mt-2 text-small text-ink/80">
                Five Seville practices examined across the full rubric.
              </p>
            </li>
          </ul>
        </div>
      </section>

      <section>
        <div className="container-wide py-16">
          <div className="mb-8 flex items-end justify-between">
            <h2 className="font-serif text-h1 text-ink">Recent and evergreen</h2>
            <Link to="/methodology" className="font-sans text-small">
              See how we evaluate &rarr;
            </Link>
          </div>
          <ol className="grid gap-x-8 gap-y-10 md:grid-cols-2">
            {recent.map((item) => (
              <li key={item.href} className="border-t border-rule pt-6">
                <div className="eyebrow mb-2">{item.kicker}</div>
                <h3 className="font-serif text-[1.5rem] leading-tight text-ink">
                  <Link to={item.href} className="no-underline hover:underline">
                    {item.title}
                  </Link>
                </h3>
                <p className="mt-3 text-ink/80">{item.dek}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </>
  );
}
